<template>
  <VDialogAdapted
    v-model="lazyValue"
    class="base-dialog"
    :class="classes"
    content-class="base-dialog-content overflow-visible"
    v-bind="$attrs"
  >
    <template
      v-for="(_, slot) of $slots"
      #[slot]="scope"
    >
      <div
        v-if="slot === 'default'"
        :key="`wrapper_${slot}`"
        class="base-dialog-wrapper"
        @mousedown="onWrapperClicked"
      >
        <div
          class="base-dialog-wrapper-content"
          :style="contentWidth ? `width: ${contentWidth}` : ''"
        >
          <BaseIcon
            v-if="slot === 'default' && close"
            :key="`slot-${slot}`"
            variant="close"
            :class="`m_position-absolute m_top-10 m_right-10 mti_m_top-0 mti_m_right-0 m_cursor-pointer m_zindex-10 ${closeClass}`"
            @click="lazyValue = null"
          />
          <slot
            :name="slot"
            v-bind="scope"
          />
        </div>
      </div>
      <slot
        v-else
        :name="slot"
        v-bind="scope"
      />
    </template>
  </VDialogAdapted>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  watch,
} from 'vue';
import BaseComponent from '@/shared/lib/uikit/components/BaseComponent/BaseComponent.vue';
import BaseIcon from '@/shared/lib/uikit/components/BaseIcon/BaseIcon.vue';
import VDialogAdapted from '@/shared/components/v3Adopted/VDialogAdapted.vue';

export default defineComponent({
  name: 'BaseDialog',
  components: { VDialogAdapted, BaseIcon },
  extends: BaseComponent,
  props: {
    /* eslint vue/require-default-prop:off */
    modelValue: null,
    value: null,
    close: {
      type: Boolean,
      default: false,
    },
    contentWidth: {
      type: [Number, String],
      default: null,
    },
    closeClass: {
      type: String,
      default: '',
    },
  },
  setup(props, { attrs, emit }) {
    const lazyValue = ref(null);

    watch(() => props.modelValue, (value: any) => {
      lazyValue.value = value;
    });

    watch(() => props.value, (value: any) => {
      lazyValue.value = value;
    });

    watch(lazyValue, () => {
      emit('update:modelValue', lazyValue.value);
      emit('input', lazyValue.value);
    });

    const onWrapperClicked = (e: PointerEvent) => {
      if (e.target === e.currentTarget && attrs.persistent === undefined) {
        lazyValue.value = null;
      }
    };

    return {
      lazyValue,
      onWrapperClicked,
    };
  },
});
</script>

<style lang="scss">
.base-dialog-wrapper-content {
  position: relative;
  max-width: 100%;
  min-width: 0;
  height: fit-content;

  &> * {
    max-width: 100%;
  }
}

.base-dialog-wrapper {
  box-sizing: border-box;
  width: 100%;
  max-height: 100%;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  scrollbar-width: none;
  padding: rem(16);

  &::-webkit-scrollbar {
    display: none;
  }
}

.v-dialog.base-dialog-content.base-dialog-content {
  box-shadow: none;
  border-radius: 0;
  position: relative;
  min-height: 0;
  display: flex;
  max-width: 100%;
  max-height: 100%;
}

@if mixin-exists("ThemeBaseDialog") {
  @include ThemeBaseDialog;
}
</style>
